import React, { useMemo } from "react";
import moment from "moment";

/**
 * GoalProgressRing — period-aware progress arc against gameConfig.dailyTarget.
 *
 * Reads togglePeriod (0=Yesterday, 1=Today, 2=Week, 3=Month) to scale the
 * target so the ring answers "how close are we to the goal *for this period*":
 *   - Today / Yesterday → dailyTarget × 1
 *   - Week             → dailyTarget × 7 (calendar week)
 *   - Month            → dailyTarget × dayOfMonth (pace: where we should be today)
 *
 * Anchor metrics (`gameConfig.type === "anchor"`) don't render a ring — the
 * card falls back to a small badge for those. This component returns null
 * defensively if it gets one, so callers can be terse.
 */
function GoalProgressRing({ currentValue, gameConfig, unit, color, togglePeriod = 3 }) {
  const dailyTarget = Number(gameConfig?.dailyTarget) || 0;
  const type = gameConfig?.type;

  const { scaledTarget, periodLabel } = useMemo(() => {
    if (togglePeriod === 0) return { scaledTarget: dailyTarget, periodLabel: "yesterday" };
    if (togglePeriod === 1) return { scaledTarget: dailyTarget, periodLabel: "today" };
    if (togglePeriod === 2) return { scaledTarget: dailyTarget * 7, periodLabel: "this week" };
    const dayOfMonth = moment().date();
    return { scaledTarget: dailyTarget * dayOfMonth, periodLabel: "monthly pace" };
  }, [togglePeriod, dailyTarget]);

  if (type === "anchor" || dailyTarget <= 0) return null;

  const value = Number(currentValue) || 0;
  const rawPct = scaledTarget > 0 ? (value / scaledTarget) * 100 : 0;
  const displayPct = Math.round(rawPct);
  const clampedPct = Math.min(rawPct, 100);

  const SIZE = 40;
  const STROKE = 3;
  const R = (SIZE - STROKE) / 2;
  const C = 2 * Math.PI * R;
  const dashOffset = C * (1 - clampedPct / 100);

  const formatValue = (v) => {
    if (unit === "currency") {
      if (v >= 1000) return `$${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`;
      return `$${Math.round(v).toLocaleString()}`;
    }
    if (unit === "percent") return `${Math.round(v)}%`;
    if (v >= 1000) return `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`;
    return Math.round(v).toLocaleString();
  };

  return (
    <div className="inline-flex items-center gap-2.5">
      <div className="relative" style={{ width: SIZE, height: SIZE }}>
        <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} className="block" aria-hidden="true">
          <circle cx={SIZE / 2} cy={SIZE / 2} r={R} fill="none" stroke="#EAECF0" strokeWidth={STROKE} />
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={R}
            fill="none"
            stroke={color || "var(--gmb-primary)"}
            strokeWidth={STROKE}
            strokeDasharray={C}
            strokeDashoffset={dashOffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
            style={{ transition: "stroke-dashoffset 600ms ease" }}
          />
        </svg>
        <span
          className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold"
          style={{ color: color || "var(--gmb-primary)", fontFamily: "DM Sans" }}
        >
          {displayPct}%
        </span>
      </div>
      <div className="flex flex-col leading-none">
        <span className="text-[11px] font-medium text-gray-700" style={{ fontFamily: "DM Sans" }}>
          of {periodLabel}
        </span>
        <span className="text-[10px] text-gray-400 font-normal mt-0.5" style={{ fontFamily: "DM Sans", fontFeatureSettings: '"tnum" 1' }}>
          {formatValue(value)} / {formatValue(scaledTarget)}
        </span>
      </div>
    </div>
  );
}

export default GoalProgressRing;
